import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Crown, Settings, CalendarClock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";

const RANK = { free: 0, mid: 1, premium: 2 };

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

function MembershipBanner({ billing, onManage, managing }) {
  if (!billing || billing.tier === "free") return null;
  const label = billing.name || billing.tier;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      data-testid="membership-banner"
      className="panel p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-primary/40">
      <div>
        <p className="overline mb-1 flex items-center gap-2"><Crown size={13} className="text-primary" /> Your membership</p>
        {billing.has_subscription ? (
          <p className="text-sm text-foreground/90">
            You're on the <span className="text-primary font-medium">{label}</span> plan — billed monthly. Manage payment, invoices or cancel any time.
          </p>
        ) : billing.pass_active ? (
          <p className="text-sm text-foreground/90 flex items-center gap-2">
            <CalendarClock size={14} className="text-primary" />
            <span><span className="text-primary font-medium">{label}</span> 30-day pass active — access until {fmtDate(billing.tier_expires_at)}. Nothing to cancel; it simply ends then.</span>
          </p>
        ) : (
          <p className="text-sm text-foreground/90">You're on the <span className="text-primary font-medium">{label}</span> plan.</p>
        )}
      </div>
      {billing.has_subscription && (
        <button data-testid="manage-plan-btn" onClick={onManage} disabled={managing}
          className="shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-primary/50 text-primary text-sm font-medium hover:bg-primary/10 transition-colors disabled:opacity-50">
          {managing ? <Loader2 size={15} className="animate-spin" /> : <Settings size={15} />}
          Manage plan
        </button>
      )}
    </motion.div>
  );
}

function TierCard({ tier, current, onCheckout, busyKey }) {
  const isCurrent = current === tier.id;
  const isUpgrade = RANK[tier.id] > RANK[current];
  const monthlyKey = `${tier.id}_monthly`;
  const passKey = `${tier.id}_pass`;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`tier-${tier.id}`}
      className={`panel p-8 flex flex-col ${tier.id === "premium" ? "border-primary/40" : ""}`}>
      {tier.id === "premium" && <Crown className="text-primary mb-2" size={20} />}
      <p className="overline mb-1">{tier.name}</p>
      <div className="mb-6">
        <span className="font-serif text-5xl text-primary">${tier.price}</span>
        {tier.price > 0 && <span className="text-muted-foreground text-sm">/mo</span>}
      </div>
      <ul className="space-y-2 mb-8 flex-1">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
            <Check size={15} className="text-primary mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <div className="text-center py-3 rounded-full border border-primary/40 text-primary text-sm font-medium" data-testid={`current-${tier.id}`}>
          Your current plan
        </div>
      ) : tier.id === "free" ? (
        <div className="text-center py-3 text-muted-foreground text-sm">Included</div>
      ) : isUpgrade ? (
        <div className="space-y-2">
          <button data-testid={`subscribe-${tier.id}`} disabled={!!busyKey}
            onClick={() => onCheckout(monthlyKey)}
            className="w-full py-3 rounded-full bg-primary text-[#0B0B12] font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
            {busyKey === monthlyKey ? <Loader2 size={15} className="animate-spin" /> : null}
            Subscribe · ${tier.price}/mo
          </button>
          {tier.id === "premium" && (
            <button data-testid={`pass-${tier.id}`} disabled={!!busyKey}
              onClick={() => onCheckout(passKey)}
              className="w-full py-3 rounded-full border border-border text-foreground/90 text-sm hover:border-primary/50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {busyKey === passKey ? <Loader2 size={15} className="animate-spin" /> : null}
              30-day pass · ${tier.price}
            </button>
          )}
        </div>
      ) : (
        <div className="text-center py-3 text-muted-foreground text-sm">Downgrade at renewal</div>
      )}
    </motion.div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [managing, setManaging] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data));
    api.get("/billing/me").then(({ data }) => setBilling(data));
  }, []);

  if (user === null) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const current = billing?.tier || "free";

  const checkout = async (lookup_key) => {
    setBusyKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout — please try again");
      setBusyKey(null);
    }
  };

  const manage = async () => {
    setManaging(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not open the billing portal — please try again");
      setManaging(false);
    }
  };

  return (
    <Shell>
      <p className="overline mb-2">Membership</p>
      <h1 className="font-serif text-5xl mb-3">Go deeper with your companion.</h1>
      <p className="text-muted-foreground mb-10 max-w-xl">
        Start free. Upgrade any time for journaling, pattern detection and compatibility. Subscribe monthly,
        cancel whenever — or grab a one-time 30-day Premium pass. Secure checkout by Stripe.
      </p>

      <MembershipBanner billing={billing} onManage={manage} managing={managing} />

      {!plans ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {plans.tiers.map((t) => (
            <TierCard key={t.id} tier={t} current={current} onCheckout={checkout} busyKey={busyKey} />
          ))}
        </div>
      )}
    </Shell>
  );
}
